import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  HiChevronDown,
  HiChevronLeft,
  HiXMark,
  HiDevicePhoneMobile,
  HiEnvelope,
} from "react-icons/hi2";
import { QuestionMarkCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import PushNotificationSetup from "../../components/PushNotificationSetup";
import FCMTokenManager from "../../components/FCMTokenManager";
import Header from "../../components/layout/Header";
import { useReminder } from "../../hooks/useReminder";
import { useAuth } from "../../contexts/AuthContext";
import { userService } from "../../services/userService";
import { UserData, NotifySettings } from "../../types/user";

// Terra Device interface for type safety
interface TerraDevice {
  id: string;
  resource: string;
  connectedAt: string;
  expired?: boolean;
}

// Default notification settings (some default to true to match backend logic)
const defaultNotifySettings: NotifySettings = {
  pushNotification: {
    logReminder: true, // Default enabled
    healthLog: true, // Default enabled
    activityLog: false,
    gettingStarted: true,
    loggingReminders: true,
    milestones: true,
    inactivityCheckIns: true,
    offers: true,
    deviceSync: true,
  },
  email: {
    logReminder: false,
    healthLog: false,
    activityLog: false,
    weeklyReport: true, // Default enabled
    monthlyReport: false,
  },
};

const notificationTypes: {
  key: keyof NotifySettings["pushNotification"];
  label: string;
  description: string;
}[] = [
  { key: "gettingStarted", label: "Getting started", description: "Help setting up your account and first logs" },
  { key: "loggingReminders", label: "Logging reminders", description: "Nudges to keep your logs and PEAK^ Score up to date" },
  { key: "milestones", label: "Milestones & insights", description: "Streaks, progress and new insights" },
  { key: "inactivityCheckIns", label: "Check-ins when inactive", description: "A check-in if you haven't logged for a while" },
  { key: "offers", label: "Offers & upgrades", description: "Plan offers and upgrade reminders" },
  { key: "deviceSync", label: "Device & sync issues", description: "Alerts when your wearable stops syncing" },
];

const NotificationSettings: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPushSetupModal, setShowPushSetupModal] = useState(false);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [saving, setSaving] = useState(false);

  // Local state for notification settings
  const [notifySettings, setNotifySettings] = useState<NotifySettings>(defaultNotifySettings);

  // Get reminder state from hook - checkbox is derived from whether reminder exists
  const { reminder, loading: reminderLoading } = useReminder();
  const hasLogReminder = !!reminder;

  // Fetch user data to check for connected wearables and notification settings
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      try {
        const { userData: data } = await userService.getUserProfile(currentUser.uid);
        setUserData(data);
        // Initialize notification settings from user data (some default to true to match backend logic)
        if (data?.notifySettings) {
          setNotifySettings({
            pushNotification: {
              logReminder: data.notifySettings.pushNotification?.logReminder ?? true,
              healthLog: data.notifySettings.pushNotification?.healthLog ?? true,
              activityLog: data.notifySettings.pushNotification?.activityLog ?? false,
              gettingStarted: data.notifySettings.pushNotification?.gettingStarted ?? true,
              loggingReminders: data.notifySettings.pushNotification?.loggingReminders ?? true,
              milestones: data.notifySettings.pushNotification?.milestones ?? true,
              inactivityCheckIns: data.notifySettings.pushNotification?.inactivityCheckIns ?? true,
              offers: data.notifySettings.pushNotification?.offers ?? true,
              deviceSync: data.notifySettings.pushNotification?.deviceSync ?? true,
            },
            email: {
              logReminder: data.notifySettings.email?.logReminder ?? false,
              healthLog: data.notifySettings.email?.healthLog ?? false,
              activityLog: data.notifySettings.email?.activityLog ?? false,
              weeklyReport: data.notifySettings.email?.weeklyReport ?? true,
              monthlyReport: data.notifySettings.email?.monthlyReport ?? false,
            },
          });
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, [currentUser?.uid]);

  // Calculate if user has connected wearables (non-expired devices)
  const hasConnectedWearable = userData?.deviceIDs
    ? (userData.deviceIDs as TerraDevice[]).some(
        (device: TerraDevice) => !device.expired
      )
    : false;

  // Save notification settings to Firebase
  const saveNotifySettings = async (newSettings: NotifySettings) => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await userService.updateUserProfile(currentUser.uid, { notifySettings: newSettings });
      setNotifySettings(newSettings);
    } catch (error) {
      console.error("Error saving notification settings:", error);
    } finally {
      setSaving(false);
    }
  };

  // Handler for push notification settings
  const handlePushSettingChange = (key: keyof NotifySettings['pushNotification'], value: boolean) => {
    const newSettings = {
      ...notifySettings,
      pushNotification: {
        ...notifySettings.pushNotification,
        [key]: value,
      },
    };
    saveNotifySettings(newSettings);
  };

  // Handler for email notification settings
  const handleEmailSettingChange = (key: keyof NotifySettings['email'], value: boolean) => {
    const newSettings = {
      ...notifySettings,
      email: {
        ...notifySettings.email,
        [key]: value,
      },
    };
    saveNotifySettings(newSettings);
  };

  return (
    <div className="w-full pt-16">
      <Header />
      <div className="container max-w-2xl mx-auto py-8">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between pb-4 px-8">
          <div className="flex items-center">
            <button
              onClick={() => navigate("/settings")}
              className="flex items-center p-2 bg-base-300 rounded-full hover:bg-base-300 transition-colors"
            >
              <HiChevronLeft className="h-6 w-6 text-base-content" />
            </button>
            <h1 className="text-3xl font-semibold text-base-content ml-4">
              Notifications
            </h1>
          </div>
          <button
            onClick={() => setShowInfoDialog(true)}
            className="ml-2 p-1 text-base-content/60 hover:text-base-content/80 transition-colors"
            aria-label="Information"
          >
            <QuestionMarkCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="mx-8">
          {/* PUSH NOTIFICATIONS ENABLED */}
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold">Push Notifications</h3>
            </div>

            {/* Setup Buttons */}
            <div className="flex flex-wrap gap-3 mb-6">
              <button
                onClick={() => setShowPushSetupModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-base-200 text-base-content/80 border border-base-300 hover:bg-base-300"
              >
                {hasLogReminder ? "Reminder Set" : "Set Reminder Time"}
              </button>

              <button
                onClick={() => navigate("/settings/wearable")}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors bg-base-200 text-base-content/80 border border-base-300 hover:bg-base-300"
              >
                {hasConnectedWearable ? "Wearable Connected" : "Connect Wearable"}
              </button>
            </div>

            {/* Notification Type Settings */}
            <div className="space-y-3 pt-4 border-t border-base-200">
              <p className="text-sm text-base-content/60 mb-3">Choose what to be notified about:</p>
              
              {/* Log Reminder Notifications */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.pushNotification.logReminder}
                  onChange={(e) => handlePushSettingChange('logReminder', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Daily Log Reminders</span>
              </label>

              {/* Health Log Notifications */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.pushNotification.healthLog}
                  onChange={(e) => handlePushSettingChange('healthLog', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Health Data Synced</span>
              </label>

              {/* Activity Log Notifications */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.pushNotification.activityLog}
                  onChange={(e) => handlePushSettingChange('activityLog', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Activity Data Synced</span>
              </label>
            </div>
          </div>

          {/* NOTIFICATION TYPES */}
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold">Notification types</h3>
            </div>

            <div className="space-y-3">
              {notificationTypes.map(({ key, label, description }) => (
                <label key={key} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox mt-0.5"
                    checked={notifySettings.pushNotification[key] ?? true}
                    onChange={(e) => handlePushSettingChange(key, e.target.checked)}
                    disabled={saving}
                  />
                  <span>
                    <span className="block text-[16px]">{label}</span>
                    <span className="block text-sm text-base-content/60">{description}</span>
                  </span>
                </label>
              ))}
            </div>

            <p className="text-sm text-base-content/60 pt-4 mt-4 border-t border-base-200">
              These switches turn off push notifications only. Messages still appear on your dashboard.
            </p>
          </div>

          {/* EMAIL NOTIFICATIONS */}
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-lg font-semibold">Email Notifications</h3>
            </div>

            {/* Active Email Settings */}
            <div className="space-y-3 mb-6">
              {/* Log Reminders */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.email.logReminder}
                  onChange={(e) => handleEmailSettingChange('logReminder', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Daily Log Reminders</span>
              </label>

              {/* Health Log Notifications */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.email.healthLog}
                  onChange={(e) => handleEmailSettingChange('healthLog', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Health Data Synced</span>
              </label>

              {/* Activity Log Notifications */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.email.activityLog}
                  onChange={(e) => handleEmailSettingChange('activityLog', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Activity Data Synced</span>
              </label>
            </div>

            {/* Reports Section */}
            <div className="pt-4 border-t border-base-200">

              {/* Weekly Reports */}
              <label className="flex items-center gap-3 mb-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.email.weeklyReport}
                  onChange={(e) => handleEmailSettingChange('weeklyReport', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Weekly Reports</span>
              </label>

              {/* Monthly Reports */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={notifySettings.email.monthlyReport}
                  onChange={(e) => handleEmailSettingChange('monthlyReport', e.target.checked)}
                  disabled={saving}
                />
                <span className="text-[16px]">Monthly Reports</span>
              </label>
            </div>
          </div>

          {/* Advanced Device Management - Collapsible */}
          <div className="bg-base-100 rounded-lg shadow-sm border border-base-300 p-8 mb-4 mt-4">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="w-full  text-left focus:outline-none  rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div>
                    <h2 className="text-lg font-semibold text-base-content">
                      Device Management
                    </h2>
                    <p className="text-sm text-base-content/70">
                      Manage notification devices and troubleshoot issues
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {showAdvanced ? (
                    <HiChevronDown className="w-5 h-5 text-base-content/50" />
                  ) : (
                    <HiChevronDown className="w-5 h-5 text-base-content/50" />
                  )}
                </div>
              </div>
            </button>

            {showAdvanced && (
              <div className="pb-6 border-t border-base-200">
                <div className="pt-4">
                  <FCMTokenManager />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Push Notification Setup Modal */}
      {showPushSetupModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowPushSetupModal(false)}
        >
          <div
            className="bg-base-100 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 pb-0 border-base-300">
              <h3 className="font-grotesk text-xl">Set Reminder</h3>
              <button
                onClick={() => setShowPushSetupModal(false)}
                className="p-2 text-base-content/50 hover:text-base-content/70 rounded-full hover:bg-base-200 transition-colors"
              >
                <HiXMark className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-10 pt-0">
              <PushNotificationSetup />
            </div>
          </div>
        </div>
      )}

      {/* Information Dialog */}
      {showInfoDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-base-100 rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-grotesk font-semibold text-base-content">
                  Notification Settings
                </h3>
                <button
                  onClick={() => setShowInfoDialog(false)}
                  className="p-1 rounded-full hover:bg-base-200 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-base-content/60" />
                </button>
              </div>

              <div className="space-y-4 text-base-content/80">
                <div>
                  <p className="text-lg font-grotesk pb-4">
                    Stay on top of your training with customizable notifications
                    for daily logs, device syncing, and performance reports.
                  </p>

                  <h4 className="font-semibold text-base-content">
                    Push Notifications
                  </h4>
                  <p className="mb-2 italic">
                    Receive real-time alerts on your device for:
                  </p>
                  <p>- Daily log reminders at your preferred time</p>
                  <p>- Wearable device data sync confirmations</p>
                  <br></br>
                  <h4 className="font-semibold text-base-content">
                    Email Notifications
                  </h4>
                  <p className="mb-2 italic">
                    Get updates delivered to your inbox:
                  </p>
                  <p>- Daily log reminders</p>
                  <p>- Weekly performance summaries</p>
                  <p>- Monthly progress reports</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationSettings;
